import type {DataUploadAllocationQuestion} from 'common/helpers/dataUpload'

import {teacherDisplayName} from './teacherReview'

const normalize = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()

/**
 * The wizard's teachers as a "Which classes?" line can name them: `e:<employee id>` and
 * `n:<full name>`, lower-cased with spaces collapsed.
 */
export const teacherKeysOf = (teachers: readonly any[] | null | undefined): Set<string> => {
  const keys = new Set<string>()
  ;(teachers ?? []).forEach((row) => {
    const employeeId = normalize(row?.employeeId ?? row?.employee_id)
    if (employeeId) keys.add(`e:${employeeId}`)
    const name = normalize(teacherDisplayName(row ?? {}))
    if (name) keys.add(`n:${name}`)
  })
  return keys
}

/** Whether a "Which classes?" line's teacher is one of `teacherKeys` (by Employee ID or name). */
export const isTeacherInList = (
  question: Pick<DataUploadAllocationQuestion, 'teacher' | 'employeeId'>,
  teacherKeys: ReadonlySet<string>
): boolean =>
  (Boolean(question.employeeId) && teacherKeys.has(`e:${normalize(question.employeeId)}`)) ||
  teacherKeys.has(`n:${normalize(question.teacher)}`)

/**
 * The "Which classes?" lines whose teacher is still in the wizard's list. A file's lines for a
 * teacher the user has since deleted have nothing left to assign: they no longer show as "N need
 * review" (Teacher Allocation, Review Teachers, the upload card) or in its dialog.
 */
export const allocationQuestionsForTeachers = (
  questions: readonly DataUploadAllocationQuestion[],
  teacherKeys: ReadonlySet<string>
): DataUploadAllocationQuestion[] =>
  questions.filter((question) => isTeacherInList(question, teacherKeys))
